'use strict';

const moment = require('moment');
const _ = require('lodash');
const XLSX = require('xlsx');

const getExportFileName = (exportConfig, entityName = '') => {
  const fileNamePattern = exportConfig.fileName.pattern || 'export';
  const displayedEntityName = entityName || exportConfig.fileName.defaultEntityName || '';
  const fileName = fileNamePattern
    .replace('<timestamp>', moment().format(exportConfig.timestampFormat))
    .replace(
      '<entityName>',
      displayedEntityName.replace(/\s/g, exportConfig.fileName.entityNameWordSeparator)
    );
  return `${fileName}.${exportConfig.fileName.extension}`;
};

/**
 * Filters out column configs required for export.
 * @param {Array} columnDefs all column configs.
 * @returns column configs required for export.
 */
const getExportedColumnConfigs = columnDefs =>
  columnDefs.filter(columnDef => _.get(columnDef, ['customExport', 'exported'], false));

/**
 * A callback function invoked once per column. Return a string to be displayed in the column header.
 * @param {Object} columnDef column config.
 * @returns a string to be displayed in the column header.
 */
const processHeaderCallback = columnDef => {
  const headerNameGetter = _.get(
    columnDef,
    ['customExport', 'headerNameGetter'],
    () => columnDef.headerName
  );
  return headerNameGetter();
};

/**
 * A callback function invoked once per cell in the grid. Return a string value to be displayed in the export.
 * For example this is useful for formatting date values.
 * @param { columnDef, row } param0 includes columnDef (column config), row.
 * @returns a string value to be displayed in the export.
 */
const processCellCallback = ({ columnDef, row }) => {
  if (_.has(columnDef, ['customExport', 'processCellCallback'])) {
    return columnDef.customExport.processCellCallback({ columnDef, row });
  }
  const field = _.get(columnDef, ['customExport', 'field'], columnDef.field);
  const value = _.get(row, field);
  return value;
};

/**
 * Returns an array of ranges that should be merged. For example [{s: {r:0, c:0}, e: {r:0, c:1}}] for [A1:B1].
 * @param { columnDefs, rows } param0 includes columnDefs (exported column configs), rows (all exported data rows).
 * @returns  an array of ranges that should be merged.
 */
const getMergedCells = ({ columnDefs, rows }) => {
  const rowsNumberWithoutHeader = rows.length;
  const mergedCells = [];
  columnDefs.forEach((columnDef, columnIndex) => {
    const rowSpanFunc = _.get(columnDef, ['customExport', 'rowSpan'], null);
    const rowSpan = rowSpanFunc ? rowSpanFunc() : 0;
    if (!rowSpan) {
      return;
    }
    let rowIndex = 1;
    const step = rowSpan - 1;
    for (; rowIndex < rowsNumberWithoutHeader; rowIndex += rowSpan) {
      mergedCells.push({
        s: { r: rowIndex, c: columnIndex },
        e: { r: rowIndex + step, c: columnIndex },
      });
    }
  });
  return mergedCells;
};

/**
 * Generates exported data.
 * @param {columnDefs, rows} param0 includes columnDefs (exported column configs), rows (all exported data rows).
 * @returns array of arrays (rows).
 */
const generateData = ({ columnDefs: columnDefList, rows }) => {
  const processDataRowFn = row =>
    columnDefList.map(columnDef => processCellCallback({ columnDef, row }));

  const exportDataHeadersRow = columnDefList.map(processHeaderCallback);
  const exportDataRows = rows.map(processDataRowFn);

  return [exportDataHeadersRow, ...exportDataRows];
};

/**
 * Download excel
 * @param {string} fileName file name.
 * @param {Array} tableDataList array of table data, each includes table(2d array of strings), processWorksheet(a function to mutate worksheet), pageName(name of the xlsx page).
 */

const writeTablesToXLSX = (fileName, tableDataList) => {
  const workbook = XLSX.utils.book_new();

  _.forEach(tableDataList, tableData => {
    const worksheet = XLSX.utils.aoa_to_sheet(tableData.table);
    if (typeof tableData.processWorksheet === 'function') {
      tableData.processWorksheet(worksheet);
    }
    XLSX.utils.book_append_sheet(workbook, worksheet, tableData.pageName || undefined);
  });

  XLSX.writeFile(workbook, fileName);
};

/**
 * Download an Excel export of the provided data.
 * @param {exportedData, exportedColumns, rows, fileName} param0 includes exportedData (unmerged export datadata), exportedColumns (column config), rows (table rows), fileName (String to use as the file name).
 */
const writeGridXLSX = ({ exportedData, exportedColumns, rows, fileName }) => {
  const ws = XLSX.utils.aoa_to_sheet(exportedData);
  ws['!merges'] = getMergedCells({ columnDefs: exportedColumns, rows });

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws);
  XLSX.writeFile(wb, fileName);
};

/**
 * Downloads an Excel export of the grid's data.
 * @param {columnDefs, rows, fileName } param0 includes columnDefs (exported column configs), rows (table rows), fileName (String to use as the file name).
 * @returns exported data.
 */
const exportGrid = ({ columnDefs, rows, fileName }) => {
  const exportedColumns = getExportedColumnConfigs(columnDefs);
  const exportedData = generateData({ columnDefs: exportedColumns, rows });
  writeGridXLSX({ exportedData, exportedColumns, rows, fileName });
  return exportedData;
};

/**
 * Extracts level 6 nodes with their hierarchy ids and assigned grid name.
 * @param {wholesaleHierarchyIdGridMap, rootNode} param0 includes wholesaleHierarchyIdGridMap (map from levelEntrykey to gridDescription), rootNode (root node of the hierarchy).
 * @returns CDT Nodes
 */
const makeCDTNodesTable = ({ wholesaleHierarchyIdGridMap, rootNode }) => {
  const cdtNodesHeader = ['Hierarchy Node', 'Grid Name'];
  const cdtNodes = [cdtNodesHeader];

  // Recursion for the sake of simplicity.
  const dfsL6Nodes = node => {
    if (node.level === 6) {
      cdtNodes.push([
        _.get(node, 'wholesaleHierarchyKeyDisplay'),
        _.get(wholesaleHierarchyIdGridMap, [node.levelEntryKey, 'gridDescription'], null),
      ]);
      return;
    }

    _.forEach(node.children, child => dfsL6Nodes(child));
  };

  dfsL6Nodes(rootNode);

  return cdtNodes;
};

/**
 * Makes grid page header row for grids export.
 * @param {Array} gridValueRanges list of objects which contains all displayed texts for the grid value ranges.
 * @returns headers row.
 */
const makeGridPageHeadersRow = gridValueRanges => [
  'Grid Name',
  'Tool Store Group',
  'PRICE BRAND CATEGORY',
  ...gridValueRanges.map(({ displayedText }) => displayedText),
];

/**
 * Makes grid page value row for grids export.
 * @param {Array} gridValueRanges list of objects which contains all displayed texts for the grid value ranges.
 * @param {any} defaultGridValue default value for the grid values.
 * @param {gridName, toolStoreGroup, priceBrandCategory, gridValues} param2 includes gridName(name of the grid), toolStoreGroup(name of the tool store group), priceBrandCategory(name of the price brand category), gridValues(grid values of the storegroup).
 * @returns store group value row.
 */
const makeStoreGroupValueRow = (
  gridValueRanges,
  defaultGridValue,
  toolStoreGroupsDescriptionMap
) => ({ gridName, toolStoreGroup, priceBrandCategory, gridValues }) => [
  gridName,
  // a fallback, in case there is no group descriptions
  _.get(toolStoreGroupsDescriptionMap, toolStoreGroup, toolStoreGroup),
  priceBrandCategory,
  ...gridValueRanges.map(({ upperLimit: rangeUpperLimit }) => {
    // It doesn't make much sense to use a hash map for searching through 118 items.
    const gridValue = gridValues.find(({ upperLimit }) => upperLimit === rangeUpperLimit);
    return _.get(gridValue, 'value', defaultGridValue);
  }),
];

const convertStoreGroupGridsToRows = (
  makeValueRowFn,
  hardcodedStoreGroupOrder,
  sortStoreGroupsFn,
  toolStoreGroupsDescriptionMap
) => ({ gridDescription, storeGroupGrids }) => {
  const rows = [];

  const generateStoreGroupGridRows = (priceBrandIdGridMap, storeGroupKey) => {
    _.forEach(priceBrandIdGridMap, (gridValues, priceBrandGroup) => {
      rows.push(
        makeValueRowFn({
          gridName: gridDescription,
          toolStoreGroup: storeGroupKey,
          priceBrandCategory: priceBrandGroup,
          gridValues,
        })
      );
    });
  };

  if (!_.isEmpty(hardcodedStoreGroupOrder) && sortStoreGroupsFn) {
    // preserve hardcodedStoreGroupOrder when exporting
    const storeGroups = _.map(storeGroupGrids, (value, storeGroupKey) => {
      return {
        storeGroupKey,
        description: _.get(toolStoreGroupsDescriptionMap, storeGroupKey, storeGroupKey),
        ...value,
      };
    });
    const sortedStoreGroups = sortStoreGroupsFn(
      storeGroups,
      hardcodedStoreGroupOrder,
      'description'
    );

    _.forEach(sortedStoreGroups, ({ storeGroupKey, grid: priceBrandIdGridMap }) =>
      generateStoreGroupGridRows(priceBrandIdGridMap, storeGroupKey)
    );
  } else {
    _.forEach(storeGroupGrids, (obj, storeGroupKey) => {
      const priceBrandIdGridMap = _.get(obj, 'grid');
      generateStoreGroupGridRows(priceBrandIdGridMap, storeGroupKey);
    });
  }

  return rows;
};

const reduceGridsToRowsForExport = ({
  gridList,
  gridValuesRanges,
  defaultGridValue,
  toolStoreGroupsDescriptionMap,
  skipHeader = false,
  // TODO: use both storegroup name and storegroup id lookup ?
  hardcodedStoreGroupOrder,
  sortStoreGroupsFn,
}) => {
  const makeValueRow = makeStoreGroupValueRow(
    gridValuesRanges,
    defaultGridValue,
    toolStoreGroupsDescriptionMap
  );
  const convertToRows = convertStoreGroupGridsToRows(
    makeValueRow,
    hardcodedStoreGroupOrder,
    sortStoreGroupsFn,
    toolStoreGroupsDescriptionMap
  );

  return _.reduce(
    gridList,
    (acc, gridItem) =>
      acc.concat(convertToRows(_.pick(gridItem, ['gridDescription', 'storeGroupGrids']))),
    skipHeader ? [] : [makeGridPageHeadersRow(gridValuesRanges)]
  );
};

/**
 * This function takes an Excel file (as a Blob), a map of headers to expand, and a map of data for expanded cells. 
 * It returns a new Excel file (as a Blob) where certain columns are replaced by multiple columns, based on the provided header and data maps. 
 * This is useful when you want to export a grid where some columns (like alerts or tension) need to be split into several sub-columns in the Excel output.
 * @param {Blob} excelBlob - Excel file as a Blob
 * @param {Object} headerMap - Map where keys match column IDs to arrays of header objects
 * @param {Object} dataMap - Map where keys match cell values to data objects
 * @returns {Promise<Blob>} - Modified Excel file as a Blob
 */
const expandExcelColumns = async (excelBlob, headerMap, dataMap) => {
  try {
    // Convert blob to array buffer
    const arrayBuffer = await excelBlob.arrayBuffer();
    
    // Parse Excel file
    const workbook = XLSX.read(new Uint8Array(arrayBuffer), { type: 'array' });
    const sheetName = workbook.SheetNames[0];
    const rows = XLSX.utils.sheet_to_json(workbook.Sheets[sheetName], { header: 1 });
    
    if (_.isEmpty(rows)) return excelBlob;
    
    // Find header row and columns to expand in a single efficient pass
    let headerRowIndex = -1;
    const columnsToExpand = {};
    let totalNewColumns = 0;
    
    // Early termination loop - find header row and stop
    for (let rowIndex = 0; rowIndex < rows.length; rowIndex++) {
      const row = rows[rowIndex];
      let foundHeaders = false;
      
      // Check if this row contains any expandable headers
      for (let colIndex = 0; colIndex < row.length; colIndex++) {
        const headerCell = row[colIndex];
        const headerKey = headerCell !== null && headerCell !== undefined ? String(headerCell) : '';

        const matchingKey = _.find(_.keys(headerMap), key => headerKey.includes(key));

        if (matchingKey) {
          // Found a header to expand - record the row index
          headerRowIndex = rowIndex;
          foundHeaders = true;
          
          // Get expansion info
          const headers = headerMap[matchingKey];
          columnsToExpand[colIndex] = {
            headers,
            width: headers.length,
            expandBy: headers.length - 1,
          };
          totalNewColumns += headers.length - 1;
        }
      }
      
      // If we found headers in this row, stop searching
      if (foundHeaders) break;
    }
    
    // If no header row found, return original file
    if (headerRowIndex === -1 || _.isEmpty(columnsToExpand)) {
      return excelBlob;
    }
    
    // Calculate new width
    const newWidth = rows[0].length + totalNewColumns;
    
    // Process rows in a single pass
    const newRows = [];
    
    // Process ALL rows with the same column insertion logic
    for (let rowIndex = 0; rowIndex < rows.length; rowIndex++) {
      const row = rows[rowIndex] || [];
      const newRow = [];
      
      // Apply the same column expansion logic to ALL rows including upper headers
      for (let colIndex = 0; colIndex < row.length; colIndex++) {
        const cellValue = row[colIndex];
        
        if (columnsToExpand[colIndex]) {
          // This column needs expansion
          const expansion = columnsToExpand[colIndex];
          
          if (rowIndex === headerRowIndex) {
            // This is the main header row - add all the expanded headers
            _.forEach(expansion.headers, header => {
              newRow.push(header.name);
            });
          } else if (rowIndex > headerRowIndex) {
            // Data row - expand cells that have data
            const cellKey = String(cellValue || '');
            const data = dataMap[cellKey];
            
            if (data) {
              // Cell has data to expand
              _.forEach(expansion.headers, header => {
                newRow.push(_.get(data, header.type, null));
              });
            } else {
              // No data - add empty cells to maintain alignment
              for (let i = 0; i < expansion.width; i++) {
                newRow.push(null);
              }
            }
          } else {
            // Upper header row - insert the original cell followed by empty cells
            newRow.push(cellValue);
            // Add empty cells to maintain alignment with expanded columns below
            for (let i = 1; i < expansion.width; i++) {
              newRow.push(null);
            }
          }
        } else {
          // Regular cell - just copy it
          newRow.push(cellValue);
        }
      }
      
      // Ensure all rows have correct width
      while (newRow.length < newWidth) {
        newRow.push(null);
      }
      
      newRows.push(newRow);
    }
    
    // Create new Excel file with expanded structure
    const newWorkbook = XLSX.utils.book_new();
    const newSheet = XLSX.utils.aoa_to_sheet(newRows);
    XLSX.utils.book_append_sheet(newWorkbook, newSheet, sheetName);
    
    // Generate and return the Excel file
    const output = XLSX.write(newWorkbook, { 
      type: 'array',
      bookType: 'xlsx',
      compression: true,
    });
    
    return new Blob([output], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
  } catch (error) {
    console.error('Error expanding Excel columns:', error);
    return excelBlob;
  }
};

/**
 * Downloads a blob as a file
 * @param {Blob} blob - The file blob
 * @param {String} fileName - The file name
 */
const downloadFile = (blob, fileName) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  link.click();
  
  setTimeout(() => URL.revokeObjectURL(url), 100);
};

module.exports = {
  getExportFileName,
  exportGrid,
  getExportedColumnConfigs,
  processHeaderCallback,
  processCellCallback,
  getMergedCells,
  generateData,
  makeCDTNodesTable,
  writeTablesToXLSX,
  reduceGridsToRowsForExport,
  makeGridPageHeadersRow,
  expandExcelColumns,
  downloadFile,
};
